/**
 * AccountFinancialCard — the customer-summary "command center" at the top of the
 * main column (via StandardDetailPage's `renderAboveMain`). The avatar + name now
 * live in the page header, so this card answers the rest at a glance:
 *   • WHERE they came from — login method (LINE/Email) + storefront
 *   • HOW VALUABLE they are — a computed value tier + lifetime spend + metric tiles
 *   • HOW ENGAGED — loyalty points/tier, customer-since, last-seen
 *   • WHAT TO DO next — quick actions (chat on LINE, call, new order)
 */

import { Link } from "react-router-dom";
import { Phone, Plus, Repeat, Heart, Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import { CARD } from "@/config/design-tokens";
import {
  useCustomerOrders,
  useCustomerActivity,
  useCustomerTopProducts,
} from "@/features/customers/api/customerHooks";
import { buildNewRoute } from "@/lib/config/docRoutes";
import { AccountIdentity, OpenInInboxButton, parseLoginIdentities, type PortalUserRow } from "./AccountIdentityCard";
import { StatTile, formatMoney, formatReadableDate } from "./customerDetailPrimitives";
import type { SalesOrder } from "@/lib/erp/types/order";

const TH_MONTHS = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

/** "ก.พ. 2026" from an ISO date (Gregorian year, to match the rest of the card). */
function thMonthYear(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return null;
  return `${TH_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

interface ViewedProduct {
  name: string;
  thumb: string | null;
}

/**
 * Up to 4 distinct products the customer recently viewed (from PostHog
 * pageviews). Product pages are `/{collection}/{sub}/{product-slug}` (≥3
 * segments) — the last segment is the product name; the collection segments map
 * to `collection_thumbs` for a small image (best-effort, no extra fetch).
 */
function recentlyViewed(
  browsing?: { event?: string | null; path?: string | null }[],
  collectionThumbs?: Record<string, string>,
): ViewedProduct[] {
  const out: ViewedProduct[] = [];
  const seen = new Set<string>();
  for (const b of browsing ?? []) {
    if (b.event !== "$pageview") continue;
    const segs = (b.path || "").split(/[?#]/)[0].split("/").filter(Boolean);
    if (segs.length < 3) continue;
    const name = decodeURIComponent(segs[segs.length - 1]).replace(/[-_]+/g, " ").trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    // Thumbnail from the collection slug(s) — most specific segment first.
    let thumb: string | null = null;
    for (let i = segs.length - 2; i >= 0 && !thumb; i--) {
      thumb = collectionThumbs?.[decodeURIComponent(segs[i])] ?? null;
    }
    out.push({ name, thumb });
    if (out.length >= 4) break;
  }
  return out;
}

/** Whole days since an ISO datetime. */
function daysSince(iso?: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso.replace(" ", "T"));
  return Number.isNaN(t) ? null : Math.floor((Date.now() - t) / 86_400_000);
}

/** RFM-lite value tier from spend + order count + recency. */
function valueTier(count: number, totalSpent: number, lastOrderDate?: string) {
  if (count === 0) return { label: "New", cls: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300" };
  const daysSince = lastOrderDate ? Math.floor((Date.now() - new Date(lastOrderDate).getTime()) / 86_400_000) : Infinity;
  if (daysSince > 120) return { label: "At-risk", cls: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" };
  if (totalSpent >= 10_000 || count >= 5) return { label: "VIP", cls: "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300" };
  return { label: "Regular", cls: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" };
}

/** Short relative recency, e.g. "today", "1d ago", "3mo ago". */
function relativeSince(iso?: string | null): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "1mo ago" : `${months}mo ago`;
}

const ACTION_BTN =
  "inline-flex shrink-0 items-center gap-2 rounded-lg border border-border/60 px-3.5 py-2 text-[13px] font-medium text-foreground transition-colors hover:bg-muted/50 disabled:opacity-60";

export function AccountFinancialCard({ formData }: { formData: Record<string, any> }) {
  const customer = (formData.name as string) || "";
  const currency = (formData.default_currency as string) || "THB";

  const { data: orders, isLoading } = useCustomerOrders(customer);
  const orderList: SalesOrder[] = orders ?? [];
  const { data: activity } = useCustomerActivity(customer);

  const lineUserId = parseLoginIdentities(formData.portal_users as PortalUserRow[] | undefined).find(
    (l) => l.method === "line",
  )?.lineUserId;

  const creditRows = Array.isArray(formData.credit_limits) ? formData.credit_limits : [];
  const creditLimit =
    creditRows.length > 0 && creditRows[0]?.credit_limit != null ? Number(creditRows[0].credit_limit) : null;

  const active = orderList.filter((o) => (o as Record<string, any>).status !== "Cancelled");
  const totalSpent = active.reduce((sum, o) => sum + (Number((o as Record<string, any>).grand_total) || 0), 0);
  const count = active.length;
  const avgOrder = count > 0 ? totalSpent / count : 0;
  const lastOrderDate = active
    .map((o) => (o as Record<string, any>).transaction_date as string | undefined)
    .filter(Boolean)
    .sort()
    .reverse()[0];
  // Unbilled balance = Σ grand_total × (1 − per_billed%). A useful "owes us" signal.
  const outstanding = active.reduce((sum, o) => {
    const rec = o as Record<string, any>;
    const billed = Math.min(Number(rec.per_billed) || 0, 100);
    return sum + (Number(rec.grand_total) || 0) * (1 - billed / 100);
  }, 0);

  const totalItems = active.reduce((sum, o) => sum + (Number((o as Record<string, any>).total_qty) || 0), 0);
  // Up to 5 most-recent orders' product thumbnails (first item image per order).
  const recentThumbs = active
    .slice()
    .sort((a, b) =>
      String((b as Record<string, any>).transaction_date || "").localeCompare(
        String((a as Record<string, any>).transaction_date || ""),
      ),
    )
    .map((o) => activity?.order_thumbs?.[(o as Record<string, any>).name as string])
    .filter((u): u is string => !!u)
    .slice(0, 5);

  const tier = valueTier(count, totalSpent, lastOrderDate);
  const seen = relativeSince(activity?.last_seen);
  const lastOrder = lastOrderDate ? formatReadableDate(lastOrderDate) : null;
  const mobile = (formData.mobile_no as string | undefined)?.trim();
  const newOrderRoute = buildNewRoute("Sales Order", { link_doctype: "Customer", link_name: customer });

  // Customer-since + purchase rhythm (avg days between orders) — from loaded orders.
  const orderDates = active
    .map((o) => (o as Record<string, any>).transaction_date as string | undefined)
    .filter((d): d is string => !!d)
    .sort();
  const customerSince = thMonthYear(orderDates[0]);
  const avgGapDays =
    orderDates.length >= 2
      ? Math.round(
          (Date.parse(orderDates[orderDates.length - 1].replace(" ", "T")) -
            Date.parse(orderDates[0].replace(" ", "T"))) /
            86_400_000 /
            (orderDates.length - 1),
        )
      : null;

  // Most-bought product + recently-viewed products (intent signals).
  const orderNames = active.map((o) => (o as Record<string, any>).name as string).filter(Boolean);
  const { data: topProducts } = useCustomerTopProducts(orderNames);
  const topProduct = topProducts?.[0];
  const viewed = recentlyViewed(activity?.browsing, activity?.collection_thumbs);

  // Follow-up nudge — overdue vs their buying rhythm, or an at-risk tier.
  const daysSinceLast = daysSince(lastOrderDate);
  const overdue =
    avgGapDays != null &&
    daysSinceLast != null &&
    daysSinceLast > Math.max(Math.round(avgGapDays * 1.5), avgGapDays + 7);
  const showNudge = count > 0 && daysSinceLast != null && (overdue || tier.label === "At-risk");

  return (
    <div className={CARD}>
      <div className="p-6">
        {/* Identity line — where they came from · value tier */}
        <div className="flex items-center justify-between gap-3">
          <AccountIdentity
            customer={customer}
            customerName={formData.customer_name as string | undefined}
            portalUsers={formData.portal_users as PortalUserRow[] | undefined}
            storefrontKey={formData.custom_primary_storefront as string | undefined}
          />
          <span className={cn("shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold", tier.cls)}>
            {tier.label}
          </span>
        </div>

        {/* Hero — lifetime spend + lifecycle (left) · recent products bought (right) */}
        <div className="mt-6 flex items-end justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Total spent
            </div>
            <div className="mt-0.5 text-[30px] font-bold leading-tight tracking-tight text-gray-900 dark:text-gray-100">
              {isLoading ? "…" : formatMoney(totalSpent, currency)}
            </div>
            {(lastOrder || seen) && (
              <div className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[13px] text-muted-foreground">
                {lastOrder && <span>Last order {lastOrder}</span>}
                {lastOrder && seen && <span className="opacity-40">·</span>}
                {seen && <span>Last seen {seen}</span>}
              </div>
            )}
            {(customerSince || avgGapDays != null) && (
              <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[13px] text-muted-foreground">
                {customerSince && <span>ลูกค้าตั้งแต่ {customerSince}</span>}
                {customerSince && avgGapDays != null && <span className="opacity-40">·</span>}
                {avgGapDays != null && <span>ซื้อทุก ~{avgGapDays} วัน</span>}
              </div>
            )}
          </div>
          {recentThumbs.length > 0 && (
            <div className="flex shrink-0 flex-col items-end gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Recently bought
              </span>
              <div className="flex items-center gap-1.5">
                {recentThumbs.map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt=""
                    loading="lazy"
                    className="h-9 w-9 rounded-lg bg-muted object-cover ring-1 ring-black/10 dark:ring-white/10"
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Follow-up nudge — proactive re-engagement when overdue / at-risk. */}
        {showNudge && (
          <div className="mt-5 flex items-center gap-3 rounded-lg border border-sky-200 bg-sky-50 px-3.5 py-2.5 dark:border-sky-900/40 dark:bg-sky-950/30">
            <Bell className="h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" />
            <div className="min-w-0 flex-1 text-[13px] text-sky-900 dark:text-sky-200">
              <span className="font-semibold">ควรทักตาม</span>{" "}
              {overdue && avgGapDays != null
                ? `· ปกติซื้อทุก ~${avgGapDays} วัน หายไป ${daysSinceLast} วันแล้ว`
                : `· ไม่ได้ซื้อมา ${daysSinceLast} วันแล้ว`}
            </div>
            {lineUserId && (
              <OpenInInboxButton
                lineUserId={lineUserId}
                label="ทักผ่าน LINE"
                className="border-sky-300 bg-white text-sky-700 hover:bg-sky-100 dark:border-sky-800 dark:bg-transparent dark:text-sky-300"
              />
            )}
          </div>
        )}

        {/* Favourite product · recently-viewed interest — intent signals. */}
        {topProduct && (
          <div className="mt-5 flex items-center gap-2 text-[13px] text-muted-foreground">
            <Repeat className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <span className="font-medium text-foreground/80">ซื้อบ่อย:</span>
            <span className="truncate">{topProduct.name}</span>
            <span className="shrink-0 opacity-60">({topProduct.qty} ชิ้น)</span>
          </div>
        )}
        {viewed.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
            <Heart className="h-4 w-4 shrink-0 text-rose-500" />
            <span className="font-medium text-foreground/80">กำลังสนใจ:</span>
            {viewed.map((v, i) => (
              <span
                key={i}
                className="flex max-w-[180px] items-center gap-1.5 rounded-full bg-muted/60 py-0.5 pl-0.5 pr-2"
              >
                {v.thumb ? (
                  <img
                    src={v.thumb}
                    alt=""
                    loading="lazy"
                    className="h-5 w-5 shrink-0 rounded-full bg-muted object-cover ring-1 ring-black/10 dark:ring-white/10"
                  />
                ) : (
                  <span className="h-5 w-5 shrink-0 rounded-full bg-muted" />
                )}
                <span className="truncate">{v.name}</span>
              </span>
            ))}
          </div>
        )}

        {/* Quick actions — one consistent button style */}
        <div className="mt-6 flex flex-wrap gap-2.5">
          <OpenInInboxButton lineUserId={lineUserId} className={ACTION_BTN} />
          {mobile && (
            <a href={`tel:${mobile}`} className={ACTION_BTN}>
              <Phone className="h-4 w-4" />
              Call
            </a>
          )}
          <Link to={newOrderRoute} className={ACTION_BTN}>
            <Plus className="h-4 w-4" />
            New order
          </Link>
        </div>

        {/* Order metric tiles */}
        <div className="mt-6 grid grid-cols-2 gap-3 border-t border-border/40 pt-6">
          <StatTile label="คำสั่งซื้อ" value={isLoading ? "…" : String(count)} />
          <StatTile label="จำนวนชิ้น" value={isLoading ? "…" : String(totalItems)} />
          <StatTile label="ยอดเฉลี่ย/ออเดอร์" value={isLoading ? "…" : formatMoney(avgOrder, currency)} />
          <StatTile
            label="ยอดค้างชำระ"
            value={isLoading ? "…" : outstanding > 0.5 ? formatMoney(outstanding, currency) : formatMoney(0, currency)}
            accent={!isLoading && outstanding > 0.5 ? "amber" : "default"}
          />
          {creditLimit != null && <StatTile label="วงเงินเครดิต" value={formatMoney(creditLimit, currency)} />}
        </div>
      </div>
    </div>
  );
}
